import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { ProductTypeIds } from "types/common-types";
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import SelectEntity from "components/SelectEntity";

import {
  SaveOrganizationCatalogSkuRequest,
  IOrganizationCatalogSku,
  ISaveOrganizationCatalogSkuRequest,
  IOrganizationDetails,
  ListResponseOfOrganizationCatalogSkuTypes,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import Field from "components/Field";
import SelectSku from "components/SelectSku";
import useGlobalContext from "hooks/globalData";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const { Form } = withTypes<ISaveOrganizationCatalogSkuRequest>();

type OrganizationCatalogSkuFormProps = {
  onCancel?: () => void;
  onOrganizationCatalogSkuSaved: () => void;
  organization: IOrganizationDetails;
  OrganizationCatalogSku?: IOrganizationCatalogSku;
  submitLabel: string;
};

const OrganizationCatalogSkuForm = ({
  onCancel,
  onOrganizationCatalogSkuSaved,
  organization,
  OrganizationCatalogSku,
  submitLabel,
}: OrganizationCatalogSkuFormProps) => {
  const { formatMessage: t } = useIntl();
  const { catalogSkuTypeService, catalogSkuService } = useGlobalContext();
  const [skuTitle, setSkuTitle] = useState<string | undefined>("");

  const styles = useStyles(useTheme());
  const init = {
    id: OrganizationCatalogSku?.id,
    organizationId: organization.id,
    skuId: OrganizationCatalogSku?.skuId,
  };

  const fetchedOrganizationCatalogSkuTypes = useQuery<ListResponseOfOrganizationCatalogSkuTypes>(
    "organizationCatalogSkuTypes",
    () =>
      catalogSkuTypeService.getOrganizationCatalogSkuTypes(
        new ListRequestOfBaseFilter({ sortField: "name" })
      )
  );

  useEffect(() => {
    setSkuTitle(OrganizationCatalogSku?.skuTitle);
  }, [OrganizationCatalogSku]);

  const onSubmit = async (values: ISaveOrganizationCatalogSkuRequest) => {
    try {
      await catalogSkuService.saveOrganizationCatalogSku(
        new SaveOrganizationCatalogSkuRequest(values)
      );
      onOrganizationCatalogSkuSaved();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="skuId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectSku
                        {...input}
                        required
                        productTypeId={ProductTypeIds.Catalog}
                        label={t({ id: "skuLabel" })}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                        }}
                        onSkuChange={(sku) => setSkuTitle(sku?.title ?? "")}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Grid item xs={12}>
                <TextField
                  disabled={true}
                  variant="standard"
                  fullWidth
                  label={t({ id: "skuTitleColumnLabel" })}
                  value={skuTitle}
                />
              </Grid>

              <Field name="organizationCatalogSkuTypeId" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectEntity
                        fetchedData={fetchedOrganizationCatalogSkuTypes}
                        value={input.value}
                        onChange={(e) => {
                          input.onChange(e);
                        }}
                        required
                        label={t({ id: "typeLabel" })}
                        labelField={"name"}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting || !values.skuId}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default OrganizationCatalogSkuForm;
